import React, { useState, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    OutlinedInput,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Paper,
    TextField,
    Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { CombinedEquipmentRequestEntity, FileStoreEntity, useUpdateCombinedEquipmentRequestMutation } from 'graphql/graphQlApiHooks';
import UploaderService from 'services/UplodaService';
import { useSnackbar } from 'notistack';
import Iconify from 'components/Iconify';
import { useDialog } from 'providers/DialogProvider';
import { CreateRequestDialog } from './CreateRequestDialog';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

interface EquipmentQuantity {
    equipmentRequestId: string;
    equipmentName: string;
    originalQuantity: number;
    newQuantity: number;
    isEditing: boolean;
}

interface Props {
    combinedRequest: CombinedEquipmentRequestEntity;
    onClose: () => void;
    onAccept: (acceptanceCertificateId: string, equipmentQuantities: Array<{equipmentRequestId: string, quantity: number, name?: string}>) => void;
    loading?: boolean;
}

export const CombinedEquipmentAcceptanceDialog: React.FC<Props> = ({
    combinedRequest,
    onClose,
    onAccept,
    loading = false
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dialog, closeDialog] = useDialog();
    
    const [acceptanceFile, setAcceptanceFile] = useState<FileStoreEntity | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [creatingRequest, setCreatingRequest] = useState(false);
    const [deletingRequestId, setDeletingRequestId] = useState<string | null>(null);

    
    const [updateCombinedEquipmentRequest] = useUpdateCombinedEquipmentRequestMutation();
    
    const [equipmentQuantities, setEquipmentQuantities] = useState<EquipmentQuantity[]>(() => {
        return combinedRequest.childRequests?.map(request => ({
            equipmentRequestId: request.id,
            equipmentName: request.name,
            originalQuantity: request.count || 0,
            newQuantity: request.count || 0,
            isEditing: false
        })) || [];
    });

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setUploadingFile(true);
        try {
            const uploadedFiles = await UploaderService.uploadFiles(event as any, {
                onUploadProgress: (progressEvent: any) => {
                }
            });
            if (uploadedFiles && uploadedFiles.length > 0) {
                setAcceptanceFile(uploadedFiles[0]);
                enqueueSnackbar('Файл акта приемки успешно загружен', { variant: 'success' });
            }
        } catch (error) {
            enqueueSnackbar('Ошибка при загрузке файла', { variant: 'error' });
            console.error('File upload error:', error);
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleQuantityChange = (equipmentRequestId: string, newQuantity: number) => {
        setEquipmentQuantities(prev => 
            prev.map(item => 
                item.equipmentRequestId === equipmentRequestId 
                    ? { ...item, newQuantity }
                    : item
            )
        );
    };

    const handleNameChange = (equipmentRequestId: string, newName: string) => {
        setEquipmentQuantities(prev => 
            prev.map(item => 
                item.equipmentRequestId === equipmentRequestId 
                    ? { ...item, equipmentName: newName }
                    : item
            )
        );
    };

    const toggleEditMode = (equipmentRequestId: string) => {
        setEquipmentQuantities(prev => 
            prev.map(item => 
                item.equipmentRequestId === equipmentRequestId 
                    ? { ...item, isEditing: !item.isEditing }
                    : item
            )
        );
    };

    const handleDeleteRequest = async (equipmentRequestId: string) => {
        setDeletingRequestId(equipmentRequestId);
        try {
            const updatedEquipmentQuantities = equipmentQuantities.filter(item => item.equipmentRequestId !== equipmentRequestId);
            const remainingChildRequestIds = updatedEquipmentQuantities.map(item => item.equipmentRequestId);

            await updateCombinedEquipmentRequest({
                variables: {
                    input: {
                        id: combinedRequest.id,
                        childRequestId: remainingChildRequestIds
                    }
                },
                refetchQueries: ['getEquipmentRequests', 'getCombinedEquipmentRequests']
            });

            setEquipmentQuantities(updatedEquipmentQuantities);

            enqueueSnackbar("Удалено из обьедененной заявки", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(`Ошибка при удалении заявки: ${error?.message}`, { variant: "error" });
            console.error("Ошибка при удалении заявки из обьедененной заявки", error);
        } finally {
            setDeletingRequestId(null);
        }
    };

    const handleCreateRequest = () => {
        setCreatingRequest(true);
        
        const handleDialogClose = () => {
            setCreatingRequest(false);
            closeDialog();
        };
        dialog({
            onClose: handleDialogClose,
            title: "Заявка на новое спортивное оборудование",
            "aria-labelledby": "scroll-dialog-title",
            "aria-describedby": "scroll-dialog-description",
            maxWidth: "md",
            fullWidth: true,
            children: (
                <CreateRequestDialog 
                    close={handleDialogClose}
                    onSuccess={async (newRequest, closeRequestDialog) => {
                        const newEquipmentQuantity: EquipmentQuantity = {
                            equipmentRequestId: newRequest.id,
                            equipmentName: newRequest.name,
                            originalQuantity: newRequest.count || 0,
                            newQuantity: newRequest.count || 0,
                            isEditing: false
                        };
                        
                        setEquipmentQuantities(prev => [newEquipmentQuantity, ...prev]);
                        
                        try {
                            const allChildRequestIds = [
                                ...combinedRequest.childRequests?.map(req => req.id) || [],
                                newRequest.id
                            ];

                            await updateCombinedEquipmentRequest({
                                variables: {
                                    input: {
                                        id: combinedRequest.id,
                                        childRequestId: allChildRequestIds
                                    }
                                },
                                refetchQueries: ['getEquipmentRequests', 'getCombinedEquipmentRequests']
                            });
                            
                            enqueueSnackbar("Заявка создана и добавлена в комбинированную заявку", { variant: "success" });
                        } catch (error) {
                            enqueueSnackbar(`Ошибка при добавлении заявки в комбинированную заявку: ${error?.message}`, { variant: "error" });
                        } finally {
                            setCreatingRequest(false);
                        }
                        
                        closeRequestDialog();
                    }}
                />
            )
        });
    };

    const handleRemoveFile = () => {
        setAcceptanceFile(null);
    };

    const handleSubmit = async () => {
        try {
            const quantities = equipmentQuantities
                .filter(item => item.newQuantity > 0)
                .map(item => {
                    const originalRequest = combinedRequest.childRequests?.find(req => req.id === item.equipmentRequestId);
                    const isNameChanged = originalRequest && originalRequest.name !== item.equipmentName;
                    
                    return {
                        equipmentRequestId: item.equipmentRequestId,
                        quantity: item.newQuantity,
                        ...(isNameChanged && { name: item.equipmentName })
                    };
                });

            onAccept(acceptanceFile?.id || null, quantities);
            
        } catch (error) {
            enqueueSnackbar(`Ошибка при обработке заявок: ${error?.message}`, { variant: "error" });
            console.error("Ошибка при обработке заявок", error);
        }
    };

    return (
        <>
            <DialogTitle>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Приемка оборудования
                </Typography>
                <Typography className='text-sm' variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                     {combinedRequest.name}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={3}>
                    {/* Загрузка акта приемки */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Акт приемки оборудования 
                        </Typography>
                        
                        {!acceptanceFile ? (
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<Iconify icon="material-symbols:upload" />}
                                disabled={uploadingFile}
                                fullWidth
                                sx={{ py: 2 }}
                            >
                                {uploadingFile ? 'Загрузка...' : 'Загрузить акт приемки'}
                                <VisuallyHiddenInput
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={handleFileUpload}
                                />
                            </Button>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Iconify icon="material-symbols:description" />
                                        <Typography variant="body2">
                                            {acceptanceFile.name}
                                        </Typography>
                                    </Box>
                                    <IconButton 
                                        size="small" 
                                        onClick={handleRemoveFile}
                                        color="error"
                                    >
                                        <Iconify icon="material-symbols:delete" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        )}
                    </Box>

                    {/* Таблица с количествами оборудования */}
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                Количество принятого оборудования
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Iconify icon="material-symbols:add" />}
                                onClick={handleCreateRequest}
                            >
                                Добавить заявку
                            </Button>
                        </Box>
                        
                        {equipmentQuantities.length > 0 ? (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Наименование оборудования</TableCell>
                                            <TableCell align="center">Заявленное количество</TableCell>
                                            <TableCell align="center">Принятое количество</TableCell>
                                            <TableCell align="center">Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {equipmentQuantities.map((item) => {
                                            const isNewRequest = item.equipmentRequestId.startsWith('temp-') || item.equipmentRequestId.startsWith('real-');
                                            return (
                                            <TableRow 
                                                key={item.equipmentRequestId}
                                                sx={isNewRequest ? { 
                                                    backgroundColor: 'action.hover',
                                                    '& td': { borderColor: 'primary.main' }
                                                } : {}}
                                            >
                                                <TableCell>
                                                    {item.isEditing ? (
                                                        <TextField
                                                            value={item.equipmentName}
                                                            onChange={(e) => handleNameChange(item.equipmentRequestId, e.target.value)}
                                                            onBlur={() => toggleEditMode(item.equipmentRequestId)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    toggleEditMode(item.equipmentRequestId);
                                                                }
                                                            }}
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                                                {item.equipmentName}
                                                                {isNewRequest && (
                                                                    <Typography 
                                                                        component="span" 
                                                                        variant="caption" 
                                                                        sx={{ 
                                                                            ml: 1, 
                                                                            color: 'primary.main', 
                                                                            fontWeight: 'bold' 
                                                                        }}
                                                                    >
                                                                        (новая)
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                            <Tooltip title="Редактировать название">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => toggleEditMode(item.equipmentRequestId)}
                                                                >
                                                                    <Iconify icon="material-symbols:edit" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.originalQuantity}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <FormControl size="small" sx={{ minWidth: 80 }}>
                                                        <OutlinedInput
                                                            value={item.newQuantity}
                                                            onChange={(e) => handleQuantityChange(item.equipmentRequestId, parseInt(e.target.value) || 0)}
                                                            type="number"
                                                            inputProps={{ min: 0 }}
                                                        />
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Удалить заявку">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleDeleteRequest(item.equipmentRequestId)}
                                                            color="error"
                                                            disabled={deletingRequestId === item.equipmentRequestId}
                                                        >
                                                            <Iconify 
                                                                icon={deletingRequestId === item.equipmentRequestId 
                                                                    ? "material-symbols:hourglass-empty" 
                                                                    : "material-symbols:delete"
                                                                } 
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Все заявки были удалены. Добавьте оборудование для принятия.
                                </Typography>
                            </Paper>
                        )}
                    </Box>

                    <Alert severity="info">
                        Загрузите акт приемки оборудования и укажите фактическое количество принятого оборудования для каждой позиции.
                    </Alert>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Отмена
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    disabled={equipmentQuantities.length === 0}
                >
                    Подтвердить принятие
                </LoadingButton>
            </DialogActions>
        </>
    );
};

